import { effectScope, readonly, ref, watch } from 'vue';
import {
  QTool,
  type DiscoveredGateway,
  type ReadoutDeviceResult,
} from '@/native/plugins/QTool';
import { getDMGCellularConnectivity } from '@/api/connectivity/ConnectivityApi';
import { useQToolConnection } from '@/composables/useQToolConnection';
import { useQToolFirmwareUpdate } from '@/composables/useQToolFirmwareUpdate';
import {
  Dmgw6DeviceFunkcheckStatus,
  useDmgw6Store,
} from '@/store/dmgw6/dmgw6Store';
import { useDeviceStore } from '@/store/entities/deviceStore';
import { useInstallationPointStore } from '@/store/entities/installationPointStore';
import { deviceTypes, submeteringDeviceTypes } from '@/enums/device';
import {
  type DMGScannedDevice,
  Dmgw6LteCheckData,
  ScanSource,
  ScanState,
} from '@/models/installationPoint/InstallationPoint';
import { useDmgw6FunkcheckSync } from '@/composables/useDmgw6FunkcheckSync';
import { useOrderStore } from '@/store/order/orderStore';

export type DeviceReadoutState =
  | { status: 'idle' }
  | { status: 'reading'; devices: ReadoutDeviceResult[] }
  | { status: 'done'; devices: ReadoutDeviceResult[] }
  | { status: 'error'; message: string; devices?: ReadoutDeviceResult[] };

export type GatewayDiscoveryState =
  | { status: 'idle' }
  | {
      status: 'scanning';
      gateways: DiscoveredGateway[];
      lteRssi: Record<string, number | null>;
      aesKeys: Record<string, string | null>;
    }
  | { status: 'error'; message: string };

export type GatewayReadoutState =
  | { status: 'idle' }
  | {
      status: 'fetching-key';
      gateway: DiscoveredGateway;
      devices?: ReadoutDeviceResult[];
    }
  | {
      status: 'connecting';
      gateway: DiscoveredGateway;
      devices?: ReadoutDeviceResult[];
    }
  | {
      status: 'reading';
      gateway: DiscoveredGateway;
      devices: ReadoutDeviceResult[];
    }
  | {
      status: 'done';
      gateway: DiscoveredGateway;
      devices: ReadoutDeviceResult[];
    }
  | {
      status: 'error';
      gateway?: DiscoveredGateway;
      message: string;
      devices?: ReadoutDeviceResult[];
    };

function snapshotGatewayPartialResults(state: GatewayReadoutState): {
  devices?: ReadoutDeviceResult[];
} {
  const devices = 'devices' in state ? state.devices : undefined;
  return devices && devices.length > 0 ? { devices } : {};
}

function snapshotDevicePartialResults(state: DeviceReadoutState): {
  devices?: ReadoutDeviceResult[];
} {
  const devices = 'devices' in state ? state.devices : undefined;
  return devices && devices.length > 0 ? { devices } : {};
}

function gatewayReadoutErrorMessage(reason?: string): string {
  switch (reason) {
    case 'INVALID_AES_KEY':
      return 'Ungültiger AES-Schlüssel.';
    case 'INVALID_SERIAL':
      return 'Ungültige Gateway-Seriennummer.';
    case 'BEACON_TIMEOUT':
      return 'Gateway-Beacon nicht empfangen. Bitte Position des Geräts prüfen.';
    case 'SCAN_FAILED':
      return 'Scan fehlgeschlagen. Bitte erneut versuchen.';
    default:
      return 'Gateway-Auslesung fehlgeschlagen.';
  }
}

const LTE_POLL_INTERVAL_MS = 10_000;
const GATEWAY_STALE_TIMEOUT_MS = 60_000;
const GATEWAY_PRUNE_INTERVAL_MS = 15_000;
const HEAVY_SYNC_INTERVAL_MS = 1_000;
const FULL_SAVE_INTERVAL_MS = 300_000;

const deviceReadout = ref<DeviceReadoutState>({ status: 'idle' });
const gatewayDiscovery = ref<GatewayDiscoveryState>({ status: 'idle' });
const gatewayReadout = ref<GatewayReadoutState>({ status: 'idle' });
let ltePollTimer: ReturnType<typeof setInterval> | null = null;
let pruneTimer: ReturnType<typeof setInterval> | null = null;
const gatewayLastSeen = new Map<string, number>();
let listenersInitialized = false;

let pendingReadoutPreservation: {
  gatewaySerial: string;
  devices: ReadoutDeviceResult[];
  qtoolDevices: ReadoutDeviceResult[];
} | null = null;

let preservedLastReadout: {
  gatewaySerial: string;
  devices: ReadoutDeviceResult[];
  qtoolDevices: ReadoutDeviceResult[];
} | null = null;

function savePreservedReadout(): void {
  const gw = gatewayReadout.value;
  const dr = deviceReadout.value;
  const gateway = 'gateway' in gw ? gw.gateway : undefined;
  const gwDevices = 'devices' in gw && gw.devices ? gw.devices : undefined;
  const qtoolDevices = 'devices' in dr && dr.devices ? dr.devices : undefined;

  if (
    !gateway ||
    ((!gwDevices || gwDevices.length === 0) &&
      (!qtoolDevices || qtoolDevices.length === 0))
  ) {
    return;
  }

  preservedLastReadout = {
    gatewaySerial: gateway.serialNumber,
    devices: gwDevices ? [...gwDevices] : [],
    qtoolDevices: qtoolDevices ? [...qtoolDevices] : [],
  };
}

function hydrateReadoutFromInstallationPoint(gatewaySerial: string): {
  gatewayDevices: ReadoutDeviceResult[];
  qtoolDevices: ReadoutDeviceResult[];
} {
  const empty = {
    gatewayDevices: [] as ReadoutDeviceResult[],
    qtoolDevices: [] as ReadoutDeviceResult[],
  };
  const deviceStore = useDeviceStore();
  const ipStore = useInstallationPointStore();

  const gatewayDevice = [...deviceStore.devices.values()].find(
    (d) =>
      d.deviceType === deviceTypes.DirectMeterGateway &&
      d.serialNumber === gatewaySerial
  );
  if (!gatewayDevice) return empty;

  const ip = [...ipStore.installationPoints.values()].find(
    (ipEntry) => ipEntry.activeDeviceId === gatewayDevice.id
  );
  if (!ip) return empty;

  const rawDevices = ip.gatewayStatus?.devices;
  const persisted: Map<string, DMGScannedDevice[]> | undefined =
    rawDevices instanceof Map
      ? rawDevices
      : rawDevices && typeof rawDevices === 'object'
        ? new Map(
            Object.entries(rawDevices as Record<string, DMGScannedDevice[]>)
          )
        : undefined;
  if (!persisted || persisted.size === 0) return empty;

  const typeBySerial = new Map<string, string>();
  for (const d of deviceStore.devices.values()) {
    if (d.serialNumber) typeBySerial.set(d.serialNumber, d.deviceType);
  }

  const gatewayDevices: ReadoutDeviceResult[] = [];
  const qtoolDevices: ReadoutDeviceResult[] = [];

  for (const [deviceSerial, entries] of persisted) {
    const type = typeBySerial.get(deviceSerial) ?? entries[0]?.deviceType ?? '';
    for (const entry of entries) {
      if (
        entry.source !== ScanSource.QTOOL &&
        entry.source !== ScanSource.QTOOL_GATEWAY
      ) {
        continue;
      }
      const record: ReadoutDeviceResult = {
        serialNumber: deviceSerial,
        type,
        rssi: entry.rssi ?? null,
        timestamp: entry.timestamp ? new Date(entry.timestamp).getTime() : null,
      };
      if (entry.source === ScanSource.QTOOL_GATEWAY) {
        gatewayDevices.push(record);
      } else {
        qtoolDevices.push(record);
      }
    }
  }

  return { gatewayDevices, qtoolDevices };
}

const pendingGatewayDevices = new Map<string, ReadoutDeviceResult>();
const pendingQtoolDevices = new Map<string, ReadoutDeviceResult>();
const pendingDmgw6Devices = new Map<string, Map<string, DMGScannedDevice[]>>();
let uiFlushScheduled = false;
let heavySyncTimer: ReturnType<typeof setTimeout> | null = null;
let lastHeavySyncAt = 0;
let lastFullSaveAt = 0;

function bufferDmgw6Device(
  gatewaySerial: string,
  deviceSerial: string,
  data: DMGScannedDevice
): void {
  let byGateway = pendingDmgw6Devices.get(gatewaySerial);
  if (!byGateway) {
    byGateway = new Map();
    pendingDmgw6Devices.set(gatewaySerial, byGateway);
  }
  const existing = byGateway.get(deviceSerial) ?? [];
  const idx = existing.findIndex((e) => e.source === data.source);
  if (idx >= 0) existing[idx] = data;
  else existing.push(data);
  byGateway.set(deviceSerial, existing);
}

function scheduleUiFlush(): void {
  if (uiFlushScheduled) return;
  uiFlushScheduled = true;
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(flushUi);
  } else {
    setTimeout(flushUi, 16);
  }
}

function flushUi(): void {
  uiFlushScheduled = false;
  let changed = false;

  if (pendingGatewayDevices.size > 0) {
    const gw = gatewayReadout.value;
    if (gw.status === 'reading' || gw.status === 'done') {
      const merged = new Map<string, ReadoutDeviceResult>();
      for (const d of gw.devices) merged.set(d.serialNumber, d);
      for (const [serial, d] of pendingGatewayDevices) merged.set(serial, d);
      gatewayReadout.value = { ...gw, devices: [...merged.values()] };
      changed = true;
    }
    pendingGatewayDevices.clear();
  }

  if (pendingQtoolDevices.size > 0) {
    const dr = deviceReadout.value;
    if (dr.status === 'reading' || dr.status === 'done') {
      const merged = new Map<string, ReadoutDeviceResult>();
      for (const d of dr.devices) merged.set(d.serialNumber, d);
      for (const [serial, d] of pendingQtoolDevices) merged.set(serial, d);
      deviceReadout.value = { ...dr, devices: [...merged.values()] };
      changed = true;
    }
    pendingQtoolDevices.clear();
  }

  if (changed) {
    recomputeFunkcheckStatuses();
  }
}

function scheduleHeavySync(): void {
  if (heavySyncTimer) return;
  const elapsed = Date.now() - lastHeavySyncAt;
  const delay = Math.max(0, HEAVY_SYNC_INTERVAL_MS - elapsed);
  heavySyncTimer = setTimeout(runHeavySync, delay);
}

function cancelHeavySync(): void {
  if (heavySyncTimer) {
    clearTimeout(heavySyncTimer);
    heavySyncTimer = null;
  }
}

function runHeavySync(isFinal = false): void {
  heavySyncTimer = null;
  lastHeavySyncAt = Date.now();

  const hadPendingDevices = pendingDmgw6Devices.size > 0;

  if (hadPendingDevices) {
    const dmgw6Store = useDmgw6Store();
    for (const [gatewaySerial, devicesBySerial] of pendingDmgw6Devices) {
      dmgw6Store.bulkUpsertGatewayScannedDevices(
        gatewaySerial,
        devicesBySerial
      );
    }
    pendingDmgw6Devices.clear();
  }

  const needsPeriodicSave =
    !isFinal && Date.now() - lastFullSaveAt >= FULL_SAVE_INTERVAL_MS;

  if (isFinal || needsPeriodicSave) {
    useDmgw6FunkcheckSync().saveGatewayDataToInstallationPoints();
    lastFullSaveAt = Date.now();
    if (needsPeriodicSave) {
      const orderStore = useOrderStore();
      if (!orderStore.isAutoSaving) {
        orderStore.autoSave();
      }
    }
  } else if (hadPendingDevices) {
    useDmgw6FunkcheckSync().writeGatewayDataToInstallationPoints();
  }

  recomputeFunkcheckStatuses();

  if (
    !isFinal &&
    !heavySyncTimer &&
    gatewayReadout.value.status === 'reading'
  ) {
    heavySyncTimer = setTimeout(runHeavySync, FULL_SAVE_INTERVAL_MS);
  }
}

function recomputeFunkcheckStatuses(): void {
  const gwState = gatewayReadout.value;
  const qtoolState = deviceReadout.value;
  if (gwState.status === 'idle') return;

  const isDone =
    gwState.status === 'done' &&
    (qtoolState.status === 'done' || qtoolState.status === 'idle');

  const gwDevices = 'devices' in gwState ? gwState.devices : [];
  const qtoolDevices = 'devices' in qtoolState ? qtoolState.devices : [];

  const gwSerials = new Set<string>();
  for (const d of gwDevices) gwSerials.add(d.serialNumber);
  const qtoolSerials = new Set<string>();
  for (const d of qtoolDevices) qtoolSerials.add(d.serialNumber);

  const updates = new Map<string, Dmgw6DeviceFunkcheckStatus>();
  for (const device of useDeviceStore().devices.values()) {
    if (!submeteringDeviceTypes.includes(device.deviceType)) continue;
    if (!device.serialNumber) continue;

    const foundByGateway = gwSerials.has(device.serialNumber);
    const foundByQtool = qtoolSerials.has(device.serialNumber);
    if (foundByGateway) {
      updates.set(device.id, Dmgw6DeviceFunkcheckStatus.success);
    } else if (foundByQtool) {
      updates.set(device.id, Dmgw6DeviceFunkcheckStatus.partial);
    } else if (isDone) {
      updates.set(device.id, Dmgw6DeviceFunkcheckStatus.error);
    } else {
      updates.set(device.id, Dmgw6DeviceFunkcheckStatus.pending);
    }
  }

  useDmgw6Store().bulkSetDeviceFunkcheckStatuses(updates);
}

function flushAll(): void {
  if (uiFlushScheduled) {
    uiFlushScheduled = false;
    flushUi();
  }
  cancelHeavySync();
  runHeavySync(true);
}

function resetReadoutBuffers(): void {
  pendingGatewayDevices.clear();
  pendingQtoolDevices.clear();
  pendingDmgw6Devices.clear();
  uiFlushScheduled = false;
  cancelHeavySync();
  lastHeavySyncAt = 0;
  lastFullSaveAt = 0;
}

function refreshBackendLte() {
  const state = gatewayDiscovery.value;
  if (state.status !== 'scanning') return;
  for (const gw of state.gateways) {
    if (!gw.serialNumber) continue;
    getDMGCellularConnectivity(gw.serialNumber)
      .then((response) => {
        const current = gatewayDiscovery.value;
        if (current.status !== 'scanning') return;
        const rssi =
          response.type === 'SUCCESS'
            ? (response.signalStrength ?? null)
            : null;
        const aesKey =
          response.type === 'SUCCESS' ? (response.aesKey ?? null) : null;
        gatewayDiscovery.value = {
          ...current,
          lteRssi: { ...current.lteRssi, [gw.serialNumber]: rssi },
          aesKeys: { ...current.aesKeys, [gw.serialNumber]: aesKey },
        };
      })
      .catch(() => {});
  }
}

function startLtePolling() {
  stopLtePolling();
  ltePollTimer = setInterval(refreshBackendLte, LTE_POLL_INTERVAL_MS);
}

function stopLtePolling() {
  if (ltePollTimer) {
    clearInterval(ltePollTimer);
    ltePollTimer = null;
  }
}

function pruneStaleGateways() {
  if (gatewayDiscovery.value.status !== 'scanning') return;
  const now = Date.now();
  const staleSerials = new Set<string>();
  for (const [serial, lastSeen] of gatewayLastSeen) {
    if (now - lastSeen > GATEWAY_STALE_TIMEOUT_MS) {
      staleSerials.add(serial);
    }
  }
  if (staleSerials.size === 0) return;

  for (const serial of staleSerials) gatewayLastSeen.delete(serial);

  const current = gatewayDiscovery.value;
  const gateways = current.gateways.filter(
    (g) => !staleSerials.has(g.serialNumber)
  );
  const lteRssi = { ...current.lteRssi };
  const aesKeys = { ...current.aesKeys };
  for (const serial of staleSerials) {
    delete lteRssi[serial];
    delete aesKeys[serial];
  }
  gatewayDiscovery.value = {
    ...current,
    gateways,
    lteRssi,
    aesKeys,
  };
}

function startPruning() {
  stopPruning();
  pruneTimer = setInterval(pruneStaleGateways, GATEWAY_PRUNE_INTERVAL_MS);
}

function stopPruning() {
  if (pruneTimer) {
    clearInterval(pruneTimer);
    pruneTimer = null;
  }
}

function setupListeners() {
  if (listenersInitialized) return;
  listenersInitialized = true;

  const scope = effectScope(true);
  scope.run(() => {
    const connection = useQToolConnection();

    watch(connection.isConnected, (connected) => {
      if (!connected) {
        if (uiFlushScheduled) {
          uiFlushScheduled = false;
          flushUi();
        }
        savePreservedReadout();
      }
      resetReadoutBuffers();
      gatewayDiscovery.value = { status: 'idle' };
      gatewayReadout.value = { status: 'idle' };
      deviceReadout.value = { status: 'idle' };
      pendingReadoutPreservation = null;

      if (!connected) {
        const dmgw6Store = useDmgw6Store();
        for (const [deviceId, status] of dmgw6Store.deviceFunkcheckStatuses) {
          if (status === Dmgw6DeviceFunkcheckStatus.pending) {
            dmgw6Store.setDeviceFunkcheckStatus(
              deviceId,
              Dmgw6DeviceFunkcheckStatus.disconnected
            );
          }
        }
      }
    });

    watch(
      () => useDeviceStore().devices.size,
      () => recomputeFunkcheckStatuses()
    );

    watch(
      () => gatewayDiscovery.value.status,
      (status) => {
        if (status === 'scanning') {
          startLtePolling();
          startPruning();
        } else {
          stopLtePolling();
          stopPruning();
        }
      }
    );

    QTool.addListener('qtoolGatewayDiscovered', (data) => {
      if (!data.serialNumber) return;
      if (gatewayDiscovery.value.status === 'scanning') {
        gatewayLastSeen.set(data.serialNumber, Date.now());
        const existing = gatewayDiscovery.value.gateways;
        const idx = existing.findIndex(
          (g) => g.serialNumber === data.serialNumber
        );
        const gateways = [...existing];
        if (idx >= 0) {
          gateways[idx] = data;
        } else {
          gateways.push(data);
        }
        gatewayDiscovery.value = { ...gatewayDiscovery.value, gateways };

        getDMGCellularConnectivity(data.serialNumber)
          .then((response) => {
            if (gatewayDiscovery.value.status !== 'scanning') return;
            const rssi =
              response.type === 'SUCCESS'
                ? (response.signalStrength ?? null)
                : null;
            const aesKey =
              response.type === 'SUCCESS' ? (response.aesKey ?? null) : null;
            gatewayDiscovery.value = {
              ...gatewayDiscovery.value,
              lteRssi: {
                ...gatewayDiscovery.value.lteRssi,
                [data.serialNumber]: rssi,
              },
              aesKeys: {
                ...gatewayDiscovery.value.aesKeys,
                [data.serialNumber]: aesKey,
              },
            };
          })
          .catch(() => {
            if (gatewayDiscovery.value.status !== 'scanning') return;
            gatewayDiscovery.value = {
              ...gatewayDiscovery.value,
              lteRssi: {
                ...gatewayDiscovery.value.lteRssi,
                [data.serialNumber]: null,
              },
              aesKeys: {
                ...gatewayDiscovery.value.aesKeys,
                [data.serialNumber]: null,
              },
            };
          });
      }
    });

    QTool.addListener('qtoolGatewayDiscoveryError', () => {});

    QTool.addListener('qtoolReadoutDevice', (data) => {
      if (deviceReadout.value.status !== 'reading') return;

      pendingQtoolDevices.set(data.serialNumber, data);
      scheduleUiFlush();

      if (
        gatewayReadout.value.status === 'reading' ||
        gatewayReadout.value.status === 'done'
      ) {
        const gatewaySerial = gatewayReadout.value.gateway.serialNumber;
        bufferDmgw6Device(gatewaySerial, data.serialNumber, {
          source: ScanSource.QTOOL,
          rssi: data.rssi ?? undefined,
          timestamp: new Date().toISOString(),
          deviceType: data.type || undefined,
        });
        scheduleHeavySync();
      }
    });

    QTool.addListener('qtoolGatewayDeviceFound', (data) => {
      if (gatewayReadout.value.status !== 'reading') return;

      pendingGatewayDevices.set(data.serialNumber, data);
      scheduleUiFlush();

      const gatewaySerial = gatewayReadout.value.gateway.serialNumber;
      bufferDmgw6Device(gatewaySerial, data.serialNumber, {
        source: ScanSource.QTOOL_GATEWAY,
        rssi: data.rssi ?? undefined,
        timestamp: new Date().toISOString(),
        deviceType: data.type || undefined,
      });
      scheduleHeavySync();
    });

    QTool.addListener('qtoolGatewayReadoutStateChanged', (data) => {
      if (gatewayReadout.value.status !== 'reading') return;

      if (data.state === 'done') {
        if (uiFlushScheduled) {
          uiFlushScheduled = false;
          flushUi();
        }
        gatewayReadout.value = {
          status: 'done',
          gateway: gatewayReadout.value.gateway,
          devices: gatewayReadout.value.devices,
        };
        if (deviceReadout.value.status === 'reading') {
          deviceReadout.value = {
            status: 'done',
            devices: deviceReadout.value.devices,
          };
        }

        const gatewaySerial = gatewayReadout.value.gateway.serialNumber;
        useDmgw6Store().setGatewayScanStatus(gatewaySerial, ScanState.FINISHED);
        cancelHeavySync();
        runHeavySync(true);
      } else if (data.state === 'error') {
        if (uiFlushScheduled) {
          uiFlushScheduled = false;
          flushUi();
        }
        gatewayReadout.value = {
          status: 'error',
          gateway: gatewayReadout.value.gateway,
          message: gatewayReadoutErrorMessage(data.reason),
          ...snapshotGatewayPartialResults(gatewayReadout.value),
        };
        cancelHeavySync();
        runHeavySync(true);
      }
    });

    QTool.addListener('qtoolReadoutStateChanged', (data) => {
      if (deviceReadout.value.status !== 'reading') return;

      if (data.state === 'idle') {
        if (uiFlushScheduled) {
          uiFlushScheduled = false;
          flushUi();
        }
        deviceReadout.value = {
          status: 'done',
          devices: deviceReadout.value.devices,
        };
        cancelHeavySync();
        runHeavySync(true);
      } else if (data.state === 'error') {
        if (uiFlushScheduled) {
          uiFlushScheduled = false;
          flushUi();
        }
        deviceReadout.value = {
          status: 'error',
          message: 'Geräteauslesung fehlgeschlagen.',
          ...snapshotDevicePartialResults(deviceReadout.value),
        };
        cancelHeavySync();
        runHeavySync(true);
      }
    });
  });
}

export function useQToolOperations(
  _connection?: ReturnType<typeof useQToolConnection>
) {
  setupListeners();

  const firmware = useQToolFirmwareUpdate();
  const firmwareBlocked = () => firmware.isBlocking.value;

  async function startDeviceReadout() {
    if (firmwareBlocked()) return;
    deviceReadout.value = { status: 'reading', devices: [] };
    try {
      await QTool.startQToolReadout();
    } catch (e) {
      deviceReadout.value = {
        status: 'error',
        message:
          e instanceof Error ? e.message : 'Geräteauslesung fehlgeschlagen.',
        ...snapshotDevicePartialResults(deviceReadout.value),
      };
    }
  }

  async function startGatewayDiscovery() {
    if (firmwareBlocked()) return;
    gatewayLastSeen.clear();
    gatewayDiscovery.value = {
      status: 'scanning',
      gateways: [],
      lteRssi: {},
      aesKeys: {},
    };
    try {
      await QTool.startGatewayDiscovery();
    } catch (e) {
      gatewayDiscovery.value = {
        status: 'error',
        message:
          e instanceof Error ? e.message : 'Gateway-Suche fehlgeschlagen.',
      };
    }
  }

  async function startGatewayReadout(gateway: DiscoveredGateway) {
    if (firmwareBlocked()) return;
    const prior = gatewayReadout.value;
    const priorGateway = 'gateway' in prior ? prior.gateway : undefined;
    const isSameGateway = priorGateway?.serialNumber === gateway.serialNumber;
    let preservedDevices =
      isSameGateway && 'devices' in prior && prior.devices
        ? [...prior.devices]
        : [];

    const priorQtool = deviceReadout.value;
    let preservedQtoolDevices =
      'devices' in priorQtool && priorQtool.devices
        ? [...priorQtool.devices]
        : [];

    if (preservedLastReadout?.gatewaySerial === gateway.serialNumber) {
      if (preservedLastReadout.devices.length > 0) {
        const merged = new Map<string, ReadoutDeviceResult>();
        for (const d of preservedLastReadout.devices)
          merged.set(d.serialNumber, d);
        for (const d of preservedDevices) merged.set(d.serialNumber, d);
        preservedDevices = [...merged.values()];
      }
      if (preservedLastReadout.qtoolDevices.length > 0) {
        const merged = new Map<string, ReadoutDeviceResult>();
        for (const d of preservedLastReadout.qtoolDevices)
          merged.set(d.serialNumber, d);
        for (const d of preservedQtoolDevices) merged.set(d.serialNumber, d);
        preservedQtoolDevices = [...merged.values()];
      }
    }

    const persisted = hydrateReadoutFromInstallationPoint(gateway.serialNumber);
    if (persisted.gatewayDevices.length > 0) {
      const merged = new Map<string, ReadoutDeviceResult>();
      for (const d of persisted.gatewayDevices) merged.set(d.serialNumber, d);
      for (const d of preservedDevices) merged.set(d.serialNumber, d);
      preservedDevices = [...merged.values()];
    }
    if (persisted.qtoolDevices.length > 0) {
      const merged = new Map<string, ReadoutDeviceResult>();
      for (const d of persisted.qtoolDevices) merged.set(d.serialNumber, d);
      for (const d of preservedQtoolDevices) merged.set(d.serialNumber, d);
      preservedQtoolDevices = [...merged.values()];
    }

    try {
      gatewayReadout.value = {
        status: 'fetching-key',
        gateway,
        ...(preservedDevices.length > 0 ? { devices: preservedDevices } : {}),
      };
      const cachedAesKey =
        gatewayDiscovery.value.status === 'scanning'
          ? (gatewayDiscovery.value.aesKeys[gateway.serialNumber] ?? null)
          : null;

      let aesKey = cachedAesKey;
      if (!aesKey) {
        const response = await getDMGCellularConnectivity(gateway.serialNumber);

        if (response.type !== 'SUCCESS') {
          gatewayReadout.value = {
            status: 'error',
            gateway,
            message: 'AES-Schlüssel konnte nicht abgerufen werden.',
            ...(preservedDevices.length > 0
              ? { devices: preservedDevices }
              : {}),
          };
          return;
        }

        if (!response.aesKey) {
          gatewayReadout.value = {
            status: 'error',
            gateway,
            message:
              'Für dieses Gateway ist kein AES-Schlüssel hinterlegt. Bitte Seriennummer prüfen oder Support kontaktieren.',
            ...(preservedDevices.length > 0
              ? { devices: preservedDevices }
              : {}),
          };
          return;
        }

        aesKey = response.aesKey;

        const lteData = new Dmgw6LteCheckData();
        lteData.rssi = response.signalStrength ?? undefined;
        lteData.quality = response.quality;
        lteData.timestamp =
          response.timestamp != null
            ? new Date(response.timestamp).toISOString()
            : new Date().toISOString();
        const dmgw6Store = useDmgw6Store();
        dmgw6Store.setGatewayLteCheckData(gateway.serialNumber, lteData);
        useDmgw6FunkcheckSync().saveGatewayDataToInstallationPoints();
      }

      gatewayReadout.value = {
        status: 'connecting',
        gateway,
        ...(preservedDevices.length > 0 ? { devices: preservedDevices } : {}),
      };
      await QTool.startGatewayReadout({
        serialNumber: gateway.serialNumber,
        aesKey,
      });

      gatewayReadout.value = {
        status: 'reading',
        gateway,
        devices: [...preservedDevices],
      };
      deviceReadout.value = {
        status: 'reading',
        devices: [...preservedQtoolDevices],
      };
      useDmgw6Store().setGatewayScanStatus(
        gateway.serialNumber,
        ScanState.RUNNING
      );
      useDmgw6FunkcheckSync().saveGatewayDataToInstallationPoints();
    } catch (e) {
      gatewayReadout.value = {
        status: 'error',
        gateway,
        message:
          e instanceof Error ? e.message : 'Gateway-Auslesung fehlgeschlagen.',
        ...snapshotGatewayPartialResults(gatewayReadout.value),
      };
    }
  }

  function cancelGatewayReadout({ notifyNative = true } = {}) {
    if (uiFlushScheduled) {
      uiFlushScheduled = false;
      flushUi();
    }
    savePreservedReadout();
    resetReadoutBuffers();
    gatewayReadout.value = { status: 'idle' };
    deviceReadout.value = { status: 'idle' };
    if (notifyNative) {
      QTool.stopReadout();
    }
  }

  return {
    deviceReadout: readonly(deviceReadout),
    startDeviceReadout,

    gatewayDiscovery: readonly(gatewayDiscovery),
    startGatewayDiscovery,

    gatewayReadout: readonly(gatewayReadout),
    startGatewayReadout,
    cancelGatewayReadout,
  };
}
